import React, { useCallback } from 'react';
import { AppButton, Box, WarningIcon, WarnModal } from '@common-fe/common-fe';

import { useSnackbar } from '@/modules/core/hooks';
import { usePlanTemplatesQuery } from '@/modules/plan/queries';

import { PlanTemplatesProvider } from './contexts/PlanTemplatesContext';
import { usePayoutOrderModalsStore } from './stores/usePayoutOrderModals.store';
import { AddServicesModalContainer } from './widgets/AddServicesModal';
import { PayoutOrder } from './components';
import { usePayoutOrderActions, usePayoutOrderPlans } from './hooks';

interface Props {
  isEditMode: boolean;
  organizationPath?: string;
}

const PayoutOrderContainer: React.FC<Props> = ({ isEditMode, organizationPath }) => {
  const { categories, handleAddServices, handleRemoveCategory, handleRemoveService } =
    usePayoutOrderActions();
  const { handleClearAllPlans } = usePayoutOrderPlans();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { formattedTemplates, isLoading: isTemplatesLoading } = usePlanTemplatesQuery(
    true,
    undefined,
    organizationPath
  );

  const isAddServiceModalVisible = usePayoutOrderModalsStore((service) => service.isAddServiceModalVisible);
  const closeAddServiceModal = usePayoutOrderModalsStore((service) => service.closeAddServiceModal);
  const isClearAllModalVisible = usePayoutOrderModalsStore((service) => service.isClearAllModalVisible);
  const closeClearAllModal = usePayoutOrderModalsStore((service) => service.closeClearAllModal);
  const isValidationTriggered = usePayoutOrderModalsStore((service) => service.isValidationTriggered);

  const handleClearAll = useCallback(() => {
    handleClearAllPlans();
    closeClearAllModal();
  }, [handleClearAllPlans, closeClearAllModal]);

  const handleRemoveServiceWithSnackbar = useCallback(
    (categoryType: string, rowId: string) => {
      const category = categories.find((category) => category.categoryType === categoryType);
      const service = category?.services.find((service) => service.rowId === rowId);

      handleRemoveService(categoryType, rowId);

      handleAddPermanentSnackbar({
        text: `${service?.name || ''} service was removed`,
        closeIcon: true,
      });
    },
    [categories, handleRemoveService, handleAddPermanentSnackbar]
  );

  return (
    <PlanTemplatesProvider formattedTemplates={formattedTemplates} isLoading={isTemplatesLoading}>
      <PayoutOrder
        categories={categories}
        onRemoveCategory={handleRemoveCategory}
        onRemoveService={handleRemoveServiceWithSnackbar}
        isEditMode={isEditMode}
        showValidationErrors={isValidationTriggered}
      />
      <AddServicesModalContainer
        visible={isAddServiceModalVisible}
        onClose={closeAddServiceModal}
        onAddServices={handleAddServices}
      />
      <WarnModal
        testId="payout-order_clear-all-modal"
        iconNode={<WarningIcon size="48px" />}
        visible={isClearAllModalVisible}
        header="Would you like to clear all?"
        helptext="This will reset all selected plan templates and their order for all added services and categories."
        onSetVisible={(visible) => {
          if (!visible) closeClearAllModal();
        }}
        cancelButtonNode={
          <Box direction="row" gap="spacing12">
            <AppButton
              testId="payout-order_clear-all-cancel"
              buttonType="secondary"
              onClick={closeClearAllModal}
              width="150px"
            >
              Cancel
            </AppButton>
            <AppButton
              testId="payout-order_clear-all-confirm"
              color="red"
              onClick={handleClearAll}
              width="150px"
            >
              Yes, Clear
            </AppButton>
          </Box>
        }
      />
    </PlanTemplatesProvider>
  );
};

export default PayoutOrderContainer;
